import React, { useState, useEffect, useMemo } from 'react';
import { getAccessToken } from '../services/auth';

// Every "UPDATED: Order Confirmation" the admin sent, reviewed: what changed, who asked,
// whether the fix was right. The same verdict and headline are on the email in Outlook
// as categories; this is the full write-up. Backend: netlify/functions/lib/order-update-review.js.

const VERDICT = {
  problem: { label: 'Problem', bg: '#fde2e1', fg: '#a4161a' },
  unexplained: { label: 'No reason on file', bg: '#fff3c4', fg: '#7a5b00' },
  check: { label: 'Check', bg: '#ffe8d6', fg: '#a14a12' },
  ok: { label: 'OK', bg: '#e3f4ea', fg: '#1f6b43' },
};
const LEVEL_DOT = { ok: '#2e9e5b', check: '#e07a2b', problem: '#c62828', unexplained: '#c9a000' };

function fnBase() {
  return window.location.hostname === 'localhost' ? 'http://localhost:8888/.netlify/functions' : '/.netlify/functions';
}
async function call(method, body) {
  const headers = { 'Content-Type': 'application/json' };
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(`${fnBase()}/order-update-review${method === 'GET' ? '?days=30' : ''}`, {
    method, headers, body: method === 'GET' ? undefined : JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.ok === false) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

const fmtWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' });
};
const money = (n) => (n == null ? '' : `$${Number(n).toFixed(2)}`);
const secs = (s) => (s == null ? '' : s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`);

// "10/21/2026 11:34 AM" (admin, Eastern) -> ISO, for sorting the timeline.
function adminIso(s) {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})\s*(AM|PM))?/.exec(String(s || ''));
  if (!m) return null;
  let h = m[4] ? Number(m[4]) % 12 : 0;
  if (m[6] === 'PM') h += 12;
  return new Date(Date.UTC(+m[3], +m[1] - 1, +m[2], h + 4, m[5] ? +m[5] : 0)).toISOString();
}

function timeline(row) {
  const d = row.details || {};
  const items = [];
  for (const n of d.notes || []) items.push({ at: adminIso(n.date), kind: 'Note', who: n.employee, text: [n.description, n.response].filter(Boolean).join(' / ') });
  for (const p of d.problems || []) items.push({ at: adminIso(p.date) || p.date, kind: 'Problem', who: p.employee, text: [p.type, p.description, p.response].filter(Boolean).join(' / ') });
  for (const c of d.calls || []) items.push({ at: c.at, kind: 'Call', who: c.agent, text: `${c.direction}, ${secs(c.seconds)}${c.abandoned ? ', abandoned' : ''}${c.notes ? ` / ${c.notes}` : ''}` });
  for (const e of d.emails || []) items.push({ at: e.at, kind: 'Email', who: e.mailbox.split('@')[0], text: `${e.direction}: ${e.subject}${e.preview ? ` / ${e.preview}` : ''}` });
  for (const p of d.payments || []) items.push({ at: p.date, kind: 'Card', who: '', text: `${p.cardType} ${money(p.amount)}` });
  items.push({ at: row.received_at, kind: 'UPDATED email', who: '', text: 'The update went to the customer', mark: true });
  return items.filter((i) => i.at).sort((a, b) => String(a.at).localeCompare(String(b.at)));
}

function Pill({ verdict }) {
  const v = VERDICT[verdict] || { label: verdict || 'Pending', bg: 'var(--gray-100)', fg: 'var(--gray-600)' };
  return <span style={{ background: v.bg, color: v.fg, fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 999, whiteSpace: 'nowrap' }}>{v.label}</span>;
}

function Detail({ row, navigateTo, onRedo, redoing }) {
  const d = row.details || {};
  const inv = d.invoice || {};
  const bc = d.at_checkout || {};
  const tl = timeline(row);
  const h = { fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--gray-500)', margin: '14px 0 6px' };
  return (
    <div style={{ padding: '4px 16px 16px', fontSize: 13, lineHeight: 1.5 }}>
      {row.summary && <p style={{ margin: '6px 0 0', fontSize: 14 }}>{row.summary}</p>}
      {d.who_asked && <p style={{ margin: '6px 0 0', color: 'var(--gray-600)' }}><b>Who asked:</b> {d.who_asked}</p>}
      {d.likely_resend && <p style={{ margin: '6px 0 0', color: '#7a5b00' }}><b>Looks like a resend done with Update.</b> Worth a word about the Resend Order Confirmation link.</p>}
      {(d.concerns || []).length > 0 && (<><div style={h}>For you</div><ul style={{ margin: 0, paddingLeft: 18 }}>{d.concerns.map((c, i) => <li key={i}>{c}</li>)}</ul></>)}
      {(d.changes || []).length > 0 && (<><div style={h}>What changed</div><ul style={{ margin: 0, paddingLeft: 18 }}>{d.changes.map((c, i) => <li key={i}>{c}</li>)}</ul></>)}

      <div style={h}>Guardrail checks</div>
      <div style={{ display: 'grid', gap: 4 }}>
        {(d.checks || []).map((c, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
            <span style={{ width: 8, height: 8, borderRadius: 999, background: LEVEL_DOT[c.level] || '#999', flex: '0 0 8px', transform: 'translateY(-1px)' }} />
            <span>{c.text}</span>
          </div>
        ))}
      </div>

      <div style={h}>Then and now</div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', fontSize: 12.5, minWidth: 420 }}>
          <thead><tr>{['', 'At checkout', 'Now'].map((t) => <th key={t} style={{ textAlign: 'left', padding: '4px 12px 4px 0', color: 'var(--gray-500)', fontWeight: 600 }}>{t}</th>)}</tr></thead>
          <tbody>
            {[
              ['Items', (bc.items || []).map((i) => `${i.qty} x ${i.name}${i.options ? ` (${i.options})` : ''}`).join('; '), (inv.items || []).map((i) => `${i.qty} x ${i.name}`).join('; ') + (inv.options && inv.options.length ? ` (${inv.options.join('; ')})` : '')],
              ['Ship method', bc.shipMethod, inv.shipMethod || (d.live_order || {}).shipMethodType],
              ['Shipping + packaging', money(bc.shipping), inv.shipCost != null ? `${money(inv.shipCost)} + ${money(inv.packing)}` : ''],
              ['Ship to', bc.shipTo, (d.customer || {}).ship_to],
              ['Delivery', '', inv.deliveryDate || (d.live_order || {}).deliveryDate],
              ['Total', money(bc.total), money(inv.total)],
            ].map(([k, a, b]) => (
              <tr key={k} style={{ borderTop: '1px solid var(--gray-100)' }}>
                <td style={{ padding: '5px 12px 5px 0', fontWeight: 600, whiteSpace: 'nowrap' }}>{k}</td>
                <td style={{ padding: '5px 12px 5px 0', color: 'var(--gray-600)' }}>{a || '—'}</td>
                <td style={{ padding: '5px 0' }}>{b || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ color: 'var(--gray-500)', fontSize: 12, marginTop: 4 }}>
        {(d.customer || {}).vip ? 'VIP' : 'Non-VIP'}
        {d.customer && d.customer.lane ? ` · zip ${d.customer.zip}: ${d.customer.lane.transit_days}-day ground, ${d.customer.lane.sat_ground ? 'Saturday ground' : 'no Saturday ground'}` : ''}
        {d.customer && d.customer.residential === false ? ' · commercial address' : ''}
      </div>

      <div style={h}>Timeline</div>
      <div style={{ display: 'grid', gap: 6 }}>
        {tl.map((t, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '108px 92px minmax(0,1fr)', gap: 8, fontWeight: t.mark ? 700 : 400 }}>
            <span style={{ color: 'var(--gray-500)', fontSize: 12 }}>{fmtWhen(t.at)}</span>
            <span style={{ fontSize: 12, color: 'var(--navy)', fontWeight: 600 }}>{t.kind}{t.who ? <span style={{ color: 'var(--gray-500)', fontWeight: 400 }}> · {t.who}</span> : null}</span>
            <span style={{ overflowWrap: 'anywhere' }}>{t.text}</span>
          </div>
        ))}
      </div>
      {d.calls_note && <div style={{ color: 'var(--gray-500)', fontSize: 12, marginTop: 6 }}>{d.calls_note}</div>}

      <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
        <button className="btn btn-outline" onClick={() => navigateTo && navigateTo('order-details', String(row.bc_order_id))}>Open order #{row.bc_order_id}</button>
        <button className="btn btn-outline" disabled={redoing} onClick={() => onRedo(row)}>{redoing ? 'Reviewing...' : 'Review again'}</button>
      </div>
      {d.ai_error && <div style={{ color: 'var(--gray-500)', fontSize: 11, marginTop: 8 }}>Write-up unavailable: {d.ai_error}</div>}
    </div>
  );
}

export default function OrderUpdates({ navigateTo }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);
  // ?page=order-updates&order=N, from the review attached to the email in Outlook.
  const [deepOrder] = useState(() => new URLSearchParams(window.location.search).get('order'));
  const [filter, setFilter] = useState('all');
  const [busy, setBusy] = useState(null);

  const load = () => call('GET').then((d) => {
    setRows(d.rows);
    if (deepOrder && !open) {
      const hit = d.rows.find((r) => String(r.bc_order_id) === String(deepOrder));
      if (hit) setOpen(hit.message_id);
    }
  }).catch((e) => setError(e.message));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const counts = useMemo(() => {
    const c = { all: 0, problem: 0, unexplained: 0, check: 0, ok: 0 };
    for (const r of rows || []) { c.all++; if (c[r.verdict] != null) c[r.verdict]++; }
    return c;
  }, [rows]);
  const shown = (rows || []).filter((r) => filter === 'all' || r.verdict === filter);

  const checkNow = async () => {
    setBusy('all'); setError(null);
    try { await call('POST', { action: 'run', hours: 24 }); await load(); } catch (e) { setError(e.message); }
    setBusy(null);
  };
  const redo = async (row) => {
    setBusy(row.message_id); setError(null);
    try { await call('POST', { action: 'run', hours: 24 * 14, redo: true, only: String(row.bc_order_id) }); await load(); } catch (e) { setError(e.message); }
    setBusy(null);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Order Updates</h2>
        <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>Every "UPDATED: Order Confirmation" and every card charge or credit with no website order behind it, last 30 days. Checked every 5 minutes.</span>
        <button className="btn btn-outline" style={{ marginLeft: 'auto' }} disabled={busy === 'all'} onClick={checkNow}>{busy === 'all' ? 'Checking...' : 'Check now'}</button>
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {['all', 'problem', 'unexplained', 'check', 'ok'].map((k) => (
          <button key={k} onClick={() => setFilter(k)} style={{
            border: '1px solid var(--gray-200)', borderRadius: 999, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
            background: filter === k ? 'var(--navy)' : '#fff', color: filter === k ? '#fff' : 'var(--gray-700)',
          }}>{k === 'all' ? 'All' : VERDICT[k].label} ({counts[k]})</button>
        ))}
      </div>

      {error && <div style={{ padding: 12, fontSize: 13, color: 'var(--red)' }}>Error: {error}</div>}
      {!rows && !error && <div style={{ padding: 16, fontSize: 13, color: 'var(--gray-500)' }}>Loading...</div>}
      {rows && shown.length === 0 && <div className="card" style={{ padding: 16, fontSize: 13, color: 'var(--gray-500)' }}>Nothing here.</div>}

      <div style={{ display: 'grid', gap: 8 }}>
        {shown.map((r) => (
          <div key={r.message_id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <button onClick={() => setOpen(open === r.message_id ? null : r.message_id)} style={{
              display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: 10, width: '100%', textAlign: 'left',
              background: 'none', border: 0, padding: '12px 16px', cursor: 'pointer', alignItems: 'center',
            }}>
              <span style={{ minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: 'var(--gray-800)' }}>{r.headline || r.error || 'Waiting to review'}</span>
                <span style={{ display: 'block', fontSize: 12, color: 'var(--gray-500)', marginTop: 2 }}>
                  #{r.bc_order_id}{r.customer_name ? ` · ${r.customer_name}` : ''} · {r.kind === 'receipt' && r.txn ? `card ${String(r.txn.type || '').toLowerCase()} $${Number(r.txn.amount || 0).toFixed(2)}` : 'updated'} {fmtWhen(r.received_at)}
                </span>
              </span>
              <Pill verdict={r.status === 'done' ? r.verdict : r.status} />
            </button>
            {open === r.message_id && r.status === 'done' && <Detail row={r} navigateTo={navigateTo} onRedo={redo} redoing={busy === r.message_id} />}
            {open === r.message_id && r.status !== 'done' && (
              <div style={{ padding: '0 16px 14px', fontSize: 13, color: 'var(--gray-600)' }}>
                {r.error || 'Not reviewed yet.'} <button className="btn btn-outline" style={{ marginLeft: 8 }} disabled={busy === r.message_id} onClick={() => redo(r)}>Review now</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
